import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  FitnessCenter as FitnessCenterIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { SharedRoutinePreview, FetchWithAuth } from '../../types';
import { API_URL } from '../../utils';
import { useLanguage } from '../../i18n/LanguageContext';
import RoutinePreviewDialog from './RoutinePreviewDialog';

interface SharedRoutinesViewProps {
  fetchWithAuth: FetchWithAuth;
  onServiceUnavailable?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`shared-routines-tabpanel-${index}`}
      aria-labelledby={`shared-routines-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SharedRoutinesView: React.FC<SharedRoutinesViewProps> = ({
  fetchWithAuth,
  onServiceUnavailable,
}) => {
  const { t } = useLanguage();
  const [tabValue, setTabValue] = useState(0);
  const [receivedShares, setReceivedShares] = useState<SharedRoutinePreview[]>([]);
  const [sentShares, setSentShares] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [previewDialog, setPreviewDialog] = useState<{
    open: boolean;
    routine: SharedRoutinePreview | null;
  }>({
    open: false,
    routine: null,
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'accept' | 'decline' | null;
    routine: SharedRoutinePreview | null;
  }>({
    open: false,
    action: null,
    routine: null,
  });
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const loadReceivedShares = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/routines/shared`);
      if (response.ok) {
        const data = await response.json();
        setReceivedShares(Array.isArray(data) ? data : []);
      } else {
        throw new Error('Failed to fetch received shares');
      }
    } catch (error) {
      console.error('Error loading received shares:', error);
      setAlert({ type: 'error', message: t('routines.loadSharedError') });
    }
  }, [fetchWithAuth, t]);

  const loadSentShares = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/routines/shared-by-me`);
      if (response.ok) {
        const data = await response.json();
        setSentShares(Array.isArray(data) ? data : []);
      } else {
        throw new Error('Failed to fetch sent shares');
      }
    } catch (error) {
      console.error('Error loading sent shares:', error);
      setAlert({ type: 'error', message: t('routines.loadSharedError') });
    }
  }, [fetchWithAuth, t]);

  const loadShares = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadReceivedShares(), loadSentShares()]);
    setLoading(false);
  }, [loadReceivedShares, loadSentShares]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePreview = (routine: SharedRoutinePreview) => {
    setPreviewDialog({
      open: true,
      routine,
    });
  };

  const handleAcceptRoutine = async (routine: SharedRoutinePreview) => {
    setActionLoading(routine.id);
    try {
      const response = await fetchWithAuth(`${API_URL}/routines/shared/${routine.id}/accept`, {
        method: 'POST',
      });
      const data = await response.json();

      if (data.success) {
        setAlert({ type: 'success', message: data.message });
        await loadReceivedShares();
        setConfirmDialog({ open: false, action: null, routine: null });
        setPreviewDialog({ open: false, routine: null });
      } else {
        setAlert({ type: 'error', message: data.message || t('routines.acceptError') });
      }
    } catch (error) {
      console.error('Error accepting routine:', error);
      setAlert({ type: 'error', message: t('routines.acceptError') });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineRoutine = async (routine: SharedRoutinePreview) => {
    setActionLoading(routine.id);
    try {
      const response = await fetchWithAuth(`${API_URL}/routines/shared/${routine.id}/decline`, {
        method: 'POST',
      });
      const data = await response.json();

      if (data.success) {
        setAlert({ type: 'success', message: data.message });
        await loadReceivedShares();
        setConfirmDialog({ open: false, action: null, routine: null });
        setPreviewDialog({ open: false, routine: null });
      } else {
        setAlert({ type: 'error', message: data.message || t('routines.declineError') });
      }
    } catch (error) {
      console.error('Error declining routine:', error);
      setAlert({ type: 'error', message: t('routines.declineError') });
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  const getTotalExercises = (routine: any): number => {
    return routine.days.reduce((total: number, day: any) => total + day.exercises.length, 0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'success';
      case 'declined':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'accepted':
        return t('routines.accepted');
      case 'declined':
        return t('routines.declined');
      case 'pending':
        return t('routines.pending');
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          {t('common.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="shared routines tabs">
          <Tab label={`${t('routines.received')} (${receivedShares.length})`} />
          <Tab label={`${t('routines.sent')} (${sentShares.length})`} />
        </Tabs>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <TabPanel value={tabValue} index={0}>
        {/* Received Shares */}
        <Typography variant="h6" gutterBottom>
          {t('routines.receivedShares')}
        </Typography>
        
        {receivedShares.length === 0 ? (
          <Alert severity="info">
            {t('routines.noReceivedShares')}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {receivedShares.map((share) => (
              <Card key={share.id} sx={{ width: 350, display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {share.shared_by_user.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1">
                        {share.shared_by_user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(share.created_at)}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="h6" gutterBottom>
                    {share.routine.name}
                  </Typography>

                  {share.routine.description && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {share.routine.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">
                        {share.routine.days.length} {t('routines.days')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FitnessCenterIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">
                        {getTotalExercises(share.routine)} {t('routines.exercises')}
                      </Typography>
                    </Box>
                  </Box>

                  {share.message && (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }}>
                      "{share.message}"
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    onClick={() => handlePreview(share)}
                    startIcon={<VisibilityIcon />}
                  >
                    {t('routines.preview')}
                  </Button>
                  <Box>
                    <Tooltip title={t('routines.decline')}>
                      <IconButton
                        size="small"
                        onClick={() => setConfirmDialog({ open: true, action: 'decline', routine: share })}
                        disabled={actionLoading === share.id}
                        color="error"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('routines.accept')}>
                      <IconButton
                        size="small"
                        onClick={() => setConfirmDialog({ open: true, action: 'accept', routine: share })}
                        disabled={actionLoading === share.id}
                        color="success"
                      >
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Sent Shares */}
        <Typography variant="h6" gutterBottom>
          {t('routines.sentShares')}
        </Typography>
        
        {sentShares.length === 0 ? (
          <Alert severity="info">
            {t('routines.noSentShares')}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {sentShares.map((share) => (
              <Card key={share.id} sx={{ width: 350, display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      {share.shared_with_user.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1">
                        {t('routines.sharedWith')} {share.shared_with_user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(share.created_at)}
                      </Typography>
                    </Box>
                    <Chip 
                      label={getStatusLabel(share.status)}
                      color={getStatusColor(share.status)}
                      size="small"
                    />
                  </Box>

                  <Typography variant="h6" gutterBottom>
                    {share.routine.name}
                  </Typography>

                  {share.routine.description && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {share.routine.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">
                        {share.routine.days.length} {t('routines.days')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FitnessCenterIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">
                        {getTotalExercises(share.routine)} {t('routines.exercises')}
                      </Typography>
                    </Box>
                  </Box>

                  {share.message && (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }}>
                      "{share.message}"
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                  {share.status === 'pending' && (
                    <Tooltip title={t('routines.cancelShare')}>
                      <IconButton
                        size="small"
                        onClick={() => {/* TODO: Implement cancel share */}}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Preview Dialog */}
      <RoutinePreviewDialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, routine: null })}
        sharedRoutine={previewDialog.routine}
        onAccept={() => previewDialog.routine && handleAcceptRoutine(previewDialog.routine)}
        onDecline={() => previewDialog.routine && handleDeclineRoutine(previewDialog.routine)}
        loading={actionLoading === previewDialog.routine?.id}
      />

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, routine: null })}
      >
        <DialogTitle>
          {confirmDialog.action === 'accept' ? t('routines.confirmAccept') : t('routines.confirmDecline')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'accept'
              ? t('routines.confirmAcceptMessage', { name: confirmDialog.routine?.routine.name })
              : t('routines.confirmDeclineMessage', { name: confirmDialog.routine?.routine.name })
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, routine: null })}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.routine && confirmDialog.action === 'accept') {
                handleAcceptRoutine(confirmDialog.routine);
              } else if (confirmDialog.routine && confirmDialog.action === 'decline') {
                handleDeclineRoutine(confirmDialog.routine);
              }
            }}
            color={confirmDialog.action === 'accept' ? 'success' : 'error'}
            variant="contained"
          >
            {confirmDialog.action === 'accept' ? t('routines.accept') : t('routines.decline')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SharedRoutinesView;